/* eslint-disable @typescript-eslint/no-explicit-any */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import Preview from './components/Preview';
import type { AdElement } from '@adflow/types';
import { CreativeHubTypes } from '@adflow/graphqlgen';

const chakraTheme = extendTheme({
  fonts: {
    heading: 'Source Sans Pro',
    body: 'Source Sans Pro',
  },
});

interface AdflowPreviewProps {
  elements: AdElement[];
  brand: CreativeHubTypes.Brand | null;
  fonts: Array<{
    fontFamily: string;
    weight: number;
    style: string;
    stretch?: string;
    src: string;
  }>;
  selectedElementId?: string | null;
  customizations?: Record<string, string> | null;
  svgIdPrefix?: string | null;
}

class AdflowPreviewWebComponent extends HTMLElement {
  private root: ReactDOM.Root | null = null;
  private _props: AdflowPreviewProps = {
    elements: [],
    brand: null,
    fonts: [],
    selectedElementId: null,
    customizations: null,
    svgIdPrefix: null,
  };

  constructor() {
    super();
  }

  static get observedAttributes() {
    return ['brand-id', 'selected-element-id'];
  }

  connectedCallback() {
    try {
      this.root = ReactDOM.createRoot(this);
      this.render();
    } catch (error) {
      console.error('AdflowPreview: Error in connectedCallback', error);
    }
  }

  disconnectedCallback() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null) {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  set elements(value: AdElement[]) {
    this._props.elements = value;
    this.scheduleRender();
  }

  get elements() {
    return this._props.elements;
  }

  set brand(value: any | null) {
    this._props.brand = value;
    this.scheduleRender();
  }

  get brand() {
    return this._props.brand;
  }

  set fonts(value: AdflowPreviewProps['fonts']) {
    this._props.fonts = value;
    this.scheduleRender();
  }

  get fonts() {
    return this._props.fonts;
  }

  set customizations(value: Record<string, string> | null) {
    this._props.customizations = value ?? null;
    this.scheduleRender();
  }

  get customizations() {
    return this._props.customizations ?? null;
  }

  set svgIdPrefix(value: string | null) {
    this._props.svgIdPrefix = value ?? null;
    this.scheduleRender();
  }

  get svgIdPrefix() {
    return this._props.svgIdPrefix ?? null;
  }

  private renderTimeout: number | null = null;

  private scheduleRender() {
    // Debounce to avoid multiple rapid re-renders
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }
    this.renderTimeout = window.setTimeout(() => {
      this.render();
    }, 0);
  }

  private render() {
    if (!this.root) {
      console.warn('AdflowPreview: No root available for rendering');
      return;
    }

    try {
      const content =
        this._props.elements.length > 0
          ? React.createElement(Preview, {
              elements: this._props.elements,
              brand: null,
              brandKit: this._props.brand,
              fonts: this._props.fonts,
              displayOptions: null,
              selectedElementId: this._props.selectedElementId,
              isIndexPreview: true, // disables overlay
              forceTopLeftOrigin: true,
              customizations: this._props.customizations,
              svgIdPrefix: this._props.svgIdPrefix,
            })
          : React.createElement(
              'div',
              {
                style: {
                  color: '#666',
                  padding: '40px',
                  textAlign: 'center',
                  fontSize: '16px',
                },
              },
              'No preview available. Select a brand to see preview.',
            );

      const wrapped = React.createElement(
        React.StrictMode,
        null,
        React.createElement(
          ChakraProvider,
          { theme: chakraTheme },
          React.createElement(
            'div',
            { style: { display: 'flex', justifyContent: 'center' } },
            content,
          ),
        ),
      );

      this.root.render(wrapped);
    } catch (error) {
      console.error('AdflowPreview: Error during render', error);
    }
  }
}

// Register the custom element
if (typeof window !== 'undefined' && !customElements.get('adflow-preview')) {
  customElements.define('adflow-preview', AdflowPreviewWebComponent);
}

export default AdflowPreviewWebComponent;
